import { DOCUMENT } from "@angular/common";
import { Component, HostListener, Inject, OnDestroy } from "@angular/core";

@Component({
  selector: "login-background",
  templateUrl: "./login-background.component.html",
  styleUrls: ["./login-background.component.scss"],
})
export class LoginBackgroundComponent implements OnDestroy {
  readonly minLoopCount = 3;
  readonly loopHeightRatio = 0.9;
  readonly endClipX = 77.745338;
  readonly scaleX = 5.6503578;
  readonly scaleY = 4.9068783;
  readonly tallTileWidth = 77.745338 * 5.6503578;
  readonly endTileWidth = 42 * 5.6503578;
  readonly tileHeight = 80.32989 * 4.9068783;
  readonly clipBottomY = 80.32989;
  readonly archPath =
    "M50.3,135.4c-5.4,0-9.7-4.4-9.7-9.7V9.7C40.6,7.6,38.9,5.9,36.8,5.9C34.7,6,33,7.7,33,9.7v115.9c0,5.4-4.4,9.7-9.7,9.7s-9.7-4.4-9.7-9.7V9.7C13.6,7.6,11.9,5.9,9.8,5.9C7.7,6,6,7.7,6,9.7V135.3H0V9.7C0,4.4,4.4,0,9.7,0C15,0,19.4,4.4,19.4,9.7v115.9c0,2.1,1.7,3.8,3.8,3.8c2.1,0,3.8-1.7,3.8-3.8V9.7C27,4.4,31.4,0,36.8,0c5.4,0,9.7,4.4,9.7,9.7v115.9c0,2.1,1.7,3.8,3.8,3.8c2.1,0,3.8-1.7,3.8-3.8V9.7c0-5.3,4.4-9.7,9.7-9.7c5.3,0,9.7,4.4,9.7,9.7v101.5c0,2.1,1.7,3.8,3.8,3.8c2.1,0,3.8-1.7,3.8-3.8V28.4c0-5.3,4.4-9.7,9.7-9.7c5.3,0,9.7,4.4,9.7,9.7v68.5c0,2.1,1.7,3.8,3.8,3.8c2.1,0,3.8-1.7,3.8-3.8v-50h6v50c0,5.3-4.4,9.7-9.7,9.7c-5.3,0-9.7-4.4-9.7-9.7V28.4c0-2.1-1.7-3.8-3.8-3.8c-2.1,0-3.8,1.7-3.8,3.8v82.9c0,5.3-4.4,9.7-9.7,9.7c-5.3,0-9.7-4.4-9.7-9.7V9.7C67.7,7.6,66,5.9,63.9,5.9C61.8,6,60,7.7,60,9.7v115.9C60.1,131,55.7,135.4,50.3,135.4z";

  readonly endLoopRevealPath = this.buildSemicircleLoopPath(
    81.1,
    100.5,
    this.clipBottomY,
    28.4
  );
  readonly endLineRevealPath = `M111.1 ${this.clipBottomY} V46.9`;
  readonly endLoopMaskStrokeWidth = 14;
  readonly endLineMaskStrokeWidth = 10;

  public loopCount = this.minLoopCount;
  public viewBox = `0 0 ${
    this.minLoopCount * this.tallTileWidth + this.endTileWidth
  } ${this.tileHeight}`;
  public endTransform = `translate(${
    this.minLoopCount * this.tallTileWidth
  } 0) scale(${this.scaleX} ${this.scaleY}) translate(${-this.endClipX} 0)`;
  private resizeFrame: number | null = null;

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.updateLoopCount();
  }

  ngOnDestroy(): void {
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (this.resizeFrame !== null) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null;
      this.updateLoopCount();
    });
  }

  get loopIndexes(): number[] {
    return Array.from({ length: this.loopCount }, (_, index) => index);
  }

  tileTransform(index: number): string {
    return `translate(${index * this.tallTileWidth} 0) scale(${this.scaleX} ${
      this.scaleY
    })`;
  }

  trackLoop(index: number): number {
    return index;
  }

  private buildSemicircleLoopPath(
    leftX: number,
    rightX: number,
    bottomY: number,
    shoulderY: number
  ): string {
    const radius = (rightX - leftX) / 2;
    return `M${leftX} ${bottomY} V${shoulderY} A${radius} ${radius} 0 0 1 ${rightX} ${shoulderY} V${bottomY}`;
  }

  private updateLoopCount(): void {
    const height = this.document.defaultView?.innerHeight ?? 800;
    const width = this.document.defaultView?.innerWidth ?? 1200;
    const svgHeight = height * this.loopHeightRatio;
    const tallScreenWidth = svgHeight * (this.tallTileWidth / this.tileHeight);
    const endScreenWidth = svgHeight * (this.endTileWidth / this.tileHeight);
    const remaining = Math.max(0, width - endScreenWidth);
    const nextCount = Math.max(
      this.minLoopCount,
      Math.ceil(remaining / tallScreenWidth) + 1
    );

    if (nextCount === this.loopCount && this.viewBox) return;
    this.loopCount = nextCount;
    this.viewBox = `0 0 ${
      this.loopCount * this.tallTileWidth + this.endTileWidth
    } ${this.tileHeight}`;
    this.endTransform = `translate(${
      this.loopCount * this.tallTileWidth
    } 0) scale(${this.scaleX} ${this.scaleY}) translate(${-this.endClipX} 0)`;
  }
}
